/**
 * Add or edit a SIEM adapter (T-11 slice 2).
 *
 * The fields come from the controller's registry (configFields of each
 * adapter). Every read of an adapter masks its secrets, so in Edit a saved
 * secret is shown only as saved: it is sent back exactly as it was shown,
 * which keeps the stored value, unless the person replaces or removes it.
 * The form never holds a saved secret, and never logs what is typed.
 *
 * A saved secret is kept only for the same destination (review of PR #377):
 * once a field that decides where events go or whose credentials are used
 * changes, every saved secret opens for typing again, and the form does not
 * send until each is typed or removed. The controller refuses the same edit
 * (siem/reconfigure.ts, 400 siem_adapter_secret_retype). A required setting
 * that was set cannot be cleared, and a stored number or yes/no keeps its
 * type.
 *
 *   POST /v1/detection/siem-adapters      → Add
 *   PUT  /v1/detection/siem-adapters/:id  → Edit
 */

import { useMemo, useState, type FormEvent } from 'react';
import { KeyRound, Loader2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  createSiemAdapter,
  updateSiemAdapter,
  type SiemAdapter,
  type SiemAdapterConfigField,
  type SiemAdapterDescriptor,
} from '@/lib/api';

import {
  SIEM_FORM_CANCEL,
  SIEM_FORM_EDIT_NOTE,
  SIEM_FORM_REQUIRED,
  SIEM_FORM_SAVE_ADD,
  SIEM_FORM_SAVE_EDIT,
  SIEM_FORM_SECRET_KEPT,
  SIEM_FORM_SECRET_REMOVE,
  SIEM_FORM_SECRET_REPLACE,
  SIEM_FORM_SECRET_RETYPE,
  SIEM_FORM_URL_PLACEHOLDER,
  SIEM_FORM_VENDOR_NOTE,
} from './siem-forwarding-copy';

const inputCls =
  'glass-lite block w-full px-3 py-1.5 mt-1 rounded-md text-xs border border-border/40 focus:outline-none focus:border-primary';
const labelCls = 'text-[10px] uppercase tracking-wider text-muted-foreground';

/**
 * The vendors the add form offers: the value the controller's create schema
 * accepts (detection/schemas.ts ConfigureSiemAdapterSchema) and the registry
 * entry whose fields dispatch reads. Others stay in the marketplace.
 */
export const SIEM_FORM_VENDORS: ReadonlyArray<{ readonly vendor: string; readonly registryId: string }> = [
  { vendor: 'splunk', registryId: 'splunk_hec' },
  { vendor: 'sentinel', registryId: 'microsoft_sentinel' },
  { vendor: 'generic_webhook', registryId: 'generic_webhook' },
  { vendor: 'ibm_qradar', registryId: 'ibm_qradar' },
  { vendor: 'arcsight_esm', registryId: 'arcsight_esm' },
  { vendor: 'logrhythm_axon', registryId: 'logrhythm_axon' },
  { vendor: 'generic_syslog_cef', registryId: 'generic_syslog_cef' },
];

/** The registry entry of a stored adapter's vendor (short names included). */
const SHORT_VENDOR: Readonly<Record<string, string>> = {
  splunk: 'splunk_hec',
  sentinel: 'microsoft_sentinel',
  qradar: 'ibm_qradar',
  elastic: 'elastic_siem',
  xsoar: 'palo_alto_xsoar',
  servicenow: 'servicenow_secops',
};

/** A value the controller shows for a saved secret: [set], or [set: kms reference]. */
export function isMaskedSecret(value: unknown): value is string {
  return typeof value === 'string' && /^\[set(?:: kms reference)?\]$/.test(value);
}

/** PEM fields (certificates and keys) get a text area. */
function isPemField(field: SiemAdapterConfigField): boolean {
  return field.type === 'secret_ref' && (/cert/i.test(field.name) || /^(clientKey|mtlsKey)/.test(field.name));
}

type SecretMode = 'kept' | 'replace' | 'removed';

/**
 * Settings that change what is sent, never where it goes; the controller's
 * list (siem/reconfigure.ts ROUTING_NEUTRAL_KEYS).
 */
const ROUTING_NEUTRAL_KEYS: ReadonlySet<string> = new Set([
  'forwardEvents', 'minFindingSeverity', 'framing', 'format', 'index', 'indexName', 'sourceType', 'sourcetype', 'tableName', 'streamName',
]);
/** Secret fields that also decide which destination is trusted. */
const TRUST_KEYS: ReadonlySet<string> = new Set(['caCertRef', 'caCertificatePem']);

/**
 * Whether text still holds a mask placeholder, as the controller reads it
 * (siem/reconfigure.ts holdsPlaceholder): [set] in any letter case, or
 * percent-encoded (%5Bset).
 */
function holdsMask(text: string): boolean {
  const t = text.toLowerCase();
  return t.includes('[set') || t.includes('%5bset');
}

/** Whether a stored value, at any depth, still holds a mask placeholder. */
function holdsMaskDeep(value: unknown, depth = 0): boolean {
  if (typeof value === 'string') return holdsMask(value);
  if (depth >= 8 || value === null || typeof value !== 'object') return false;
  return Object.values(value as Record<string, unknown>).some((v) => holdsMaskDeep(v, depth + 1));
}

/** Deep equality for configuration values (text, numbers, lists, objects). */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  // The form trims what it sends; surrounding spaces are not a change, as on
  // the controller.
  if (typeof a === 'string' && typeof b === 'string') return a.trim() === b.trim();
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

function blank(v: unknown): boolean {
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
}

/**
 * The text typed for a field, as the stored type when the stored value was a
 * number or a yes/no. The same rule as the controller's inStoredType
 * (siem/reconfigure.ts), so the two agree on what an edit changes.
 */
function typed(storedValue: unknown, text: string): unknown {
  if (typeof storedValue === 'number') {
    const n = Number(text);
    return text.trim() !== '' && Number.isFinite(n) ? n : text;
  }
  if (typeof storedValue === 'boolean') {
    if (text.trim() === 'true') return true;
    if (text.trim() === 'false') return false;
  }
  return text;
}

interface Props {
  readonly registry: ReadonlyArray<SiemAdapterDescriptor>;
  /** Absent for Add. */
  readonly adapter?: SiemAdapter;
  readonly onSaved: (adapter: SiemAdapter) => void;
  readonly onCancel: () => void;
}

export function SiemAdapterForm({ registry, adapter, onSaved, onCancel }: Props) {
  const editing = adapter !== undefined;
  const offered = useMemo(
    () =>
      SIEM_FORM_VENDORS.map((v) => ({ ...v, descriptor: registry.find((d) => d.id === v.registryId) })).filter(
        (v): v is typeof v & { descriptor: SiemAdapterDescriptor } => v.descriptor !== undefined,
      ),
    [registry],
  );
  const [vendor, setVendor] = useState<string>(adapter?.vendor ?? offered[0]?.vendor ?? '');
  const stored = adapter?.configuration ?? {};

  const descriptor = useMemo(() => {
    const id = editing
      ? (SHORT_VENDOR[vendor] ?? vendor)
      : (SIEM_FORM_VENDORS.find((v) => v.vendor === vendor)?.registryId ?? vendor);
    return registry.find((d) => d.id === id);
  }, [editing, registry, vendor]);

  // Registry fields, then (in Edit) any stored key the registry does not
  // list, so saving never drops a setting silently.
  const fields: SiemAdapterConfigField[] = useMemo(() => {
    const own = [...(descriptor?.configFields ?? [])];
    if (editing) {
      for (const [name, value] of Object.entries(stored)) {
        if (own.some((f) => f.name === name)) continue;
        own.push({
          name,
          type: isMaskedSecret(value) ? 'secret_ref' : 'string',
          required: false,
          description: 'A setting this adapter has that the catalogue does not list.',
        });
      }
    }
    return own;
  }, [descriptor, editing, stored]);

  const initialValues = (): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const [name, value] of Object.entries(stored)) {
      if (typeof value === 'string' && !isMaskedSecret(value)) out[name] = value;
      else if (typeof value === 'number' || typeof value === 'boolean') out[name] = String(value);
    }
    return out;
  };
  const initialSecrets = (): Record<string, SecretMode> => {
    const out: Record<string, SecretMode> = {};
    for (const [name, value] of Object.entries(stored)) if (isMaskedSecret(value)) out[name] = 'kept';
    return out;
  };
  const [values, setValues] = useState<Record<string, string>>(initialValues);
  const [secrets, setSecrets] = useState<Record<string, SecretMode>>(initialSecrets);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function onVendorChange(next: string): void {
    setVendor(next);
    setValues({});
    setSecrets({});
    setError(null);
  }

  /** The configuration the fields hold now, before any check. */
  function assemble(): Record<string, unknown> {
    const configuration: Record<string, unknown> = {};
    for (const f of fields) {
      const mode = secrets[f.name];
      if (mode === 'kept') {
        // Sent back exactly as shown: the controller keeps the stored value.
        configuration[f.name] = stored[f.name];
        continue;
      }
      if (mode === 'removed') continue;
      const raw = values[f.name] ?? '';
      const value = isPemField(f) ? raw : raw.trim();
      if (value.trim().length === 0 && mode === 'replace' && isMaskedSecret(stored[f.name])) {
        // Replace opened and left empty: nothing typed, so the saved value stays.
        configuration[f.name] = stored[f.name];
        continue;
      }
      if (value.trim().length === 0) {
        const original = stored[f.name];
        // A stored value the form cannot show as text (an object) is kept.
        if (editing && original !== undefined && typeof original === 'object') configuration[f.name] = original;
        continue;
      }
      configuration[f.name] = editing ? typed(stored[f.name], value) : value;
    }
    return configuration;
  }

  /**
   * What the controller would see as changed (siem/reconfigure.ts): every key
   * of the saved or the new configuration whose value differs (a key left out
   * is a removal), compared as shown. A kept secret is sent back as shown, so
   * it is no change. The same keys count as a destination as on the
   * controller: any key that is not a secret and not routing-neutral, plus
   * the CA keys; a key the registry does not list included, and an
   * object-valued one.
   */
  function movedKeys(configuration: Record<string, unknown>): string[] {
    const keys = new Set([...Object.keys(stored), ...Object.keys(configuration)]);
    return [...keys].filter((k) => {
      if (sameValue(configuration[k], stored[k])) return false;
      if (TRUST_KEYS.has(k)) return true;
      if (ROUTING_NEUTRAL_KEYS.has(k)) return false;
      const field = fields.find((f) => f.name === k);
      const secret = field?.type === 'secret_ref' || isMaskedSecret(stored[k]) || isMaskedSecret(configuration[k]);
      return !secret;
    });
  }
  const current = editing ? assemble() : {};
  const destinationChanged = editing && movedKeys(current).length > 0;
  /** Saved secrets that would be sent back masked; refused once the destination changed. */
  /**
   * Values sent back holding a saved part the screen only shows as a mask: a
   * saved secret ([set]) or a URL with masked parts (https://[set]@host). The
   * controller keeps each one only for the same destination, as it counts
   * kept keys (siem/reconfigure.ts), so the form asks for them too (review 1
   * of PR #377 at 0f75f3d).
   */
  const keptSecrets = Object.keys(current).filter((k) => holdsMaskDeep(stored[k]) && sameValue(current[k], stored[k]));

  /** The configuration to send, or why it cannot be sent. */
  function build():
    | { configuration: Record<string, unknown> }
    | { missing: string }
    | { retype: string[] }
    | { placeholder: string } {
    const configuration = assemble();
    const missing = editing
      ? // In Edit: a required setting that was saved cannot be cleared (a
        // secret can, to switch to another credential).
        fields.find((f) => f.required && f.type !== 'secret_ref' && !blank(stored[f.name]) && blank(configuration[f.name]))
      : fields.find((f) => f.required && configuration[f.name] === undefined);
    if (missing) return { missing: missing.name };
    // A URL shown with its credentials masked ([set]@host) and then edited
    // around the mask: the controller would refuse it as a placeholder. Say
    // so here, naming the field.
    if (editing) {
      const masked = fields.find(
        (f) =>
          f.type !== 'secret_ref' &&
          typeof configuration[f.name] === 'string' &&
          holdsMask(String(configuration[f.name])) &&
          configuration[f.name] !== stored[f.name],
      );
      if (masked) return { placeholder: masked.name };
    }
    // Checked after the required settings, as the controller does.
    if (destinationChanged && keptSecrets.length > 0) return { retype: keptSecrets };
    return { configuration };
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    const built = build();
    if ('missing' in built) {
      setError(`${built.missing} ${SIEM_FORM_REQUIRED}`);
      return;
    }
    if ('placeholder' in built) {
      setError(`${built.placeholder}: ${SIEM_FORM_URL_PLACEHOLDER}`);
      return;
    }
    if ('retype' in built) {
      // Open each saved secret for typing, and empty each URL that holds a
      // masked part so it is typed again in full; nothing is sent.
      const secretNames = built.retype.filter((name) => isMaskedSecret(stored[name]));
      const textNames = built.retype.filter((name) => !isMaskedSecret(stored[name]) && typeof stored[name] === 'string');
      setSecrets((s) => {
        const next = { ...s };
        for (const name of secretNames) next[name] = 'replace';
        return next;
      });
      setValues((v) => {
        const next = { ...v };
        for (const name of textNames) next[name] = '';
        return next;
      });
      setError(`${built.retype.join(', ')}: ${SIEM_FORM_SECRET_RETYPE}`);
      return;
    }
    setSaving(true);
    try {
      const saved = editing
        ? await updateSiemAdapter(adapter.id, built.configuration)
        : await createSiemAdapter({ vendor, configuration: built.configuration });
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The adapter was not saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid="siem-adapter-form" aria-label={editing ? 'Edit SIEM adapter' : 'Add SIEM adapter'}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-semibold">{editing ? `Edit ${adapter.vendor} adapter` : 'Add a SIEM adapter'}</div>
        <Button type="button" variant="glass" size="sm" onClick={onCancel} aria-label={SIEM_FORM_CANCEL}>
          <X className="h-3 w-3" />
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">{editing ? SIEM_FORM_EDIT_NOTE : SIEM_FORM_VENDOR_NOTE}</p>

      {!editing && (
        <div>
          <label className={labelCls} htmlFor="siem-form-vendor">Vendor</label>
          <select
            id="siem-form-vendor"
            value={vendor}
            onChange={(e) => onVendorChange(e.target.value)}
            className={`${inputCls} bg-transparent`}
          >
            {offered.map((v) => (
              <option key={v.vendor} value={v.vendor}>{v.descriptor.displayName}</option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {fields.map((f) => {
          const id = `siem-form-${f.name}`;
          const mode = secrets[f.name];
          const label = (
            <label className={labelCls} htmlFor={id}>
              <span className="font-mono normal-case">{f.name}</span>
              {f.required && (!editing || (f.type !== 'secret_ref' && !blank(stored[f.name]))) ? ' (required)' : ''}
            </label>
          );
          const help = <p className="text-[10px] text-muted-foreground mt-1">{f.description}</p>;
          // A saved secret opened for typing (Replace, or after a destination
          // change) can still be removed instead: the retype message offers
          // both (review 2 of PR #377 at 0f75f3d).
          const removeSaved =
            mode === 'replace' && isMaskedSecret(stored[f.name]) ? (
              <Button
                type="button"
                variant="glass"
                size="sm"
                className="mt-1"
                data-testid={`siem-form-remove-${f.name}`}
                onClick={() => {
                  setSecrets((s) => ({ ...s, [f.name]: 'removed' }));
                  setValues((v) => ({ ...v, [f.name]: '' }));
                }}
              >
                {SIEM_FORM_SECRET_REMOVE}
              </Button>
            ) : null;
          if (mode === 'kept' || mode === 'removed') {
            return (
              <div key={f.name} className={isPemField(f) ? 'md:col-span-2' : undefined} data-testid={`siem-form-secret-${f.name}`}>
                <div className={labelCls}><span className="font-mono normal-case">{f.name}</span></div>
                <div className="flex items-center gap-2 mt-1 text-[11px]">
                  <KeyRound className="h-3 w-3 text-muted-foreground shrink-0" />
                  <span className="flex-1">
                    {mode === 'kept'
                      ? destinationChanged
                        ? `${String(stored[f.name])}. ${SIEM_FORM_SECRET_RETYPE}`
                        : `${String(stored[f.name])}. ${SIEM_FORM_SECRET_KEPT}`
                      : 'Will be removed when you save.'}
                  </span>
                  <Button
                    type="button"
                    variant="glass"
                    size="sm"
                    onClick={() => setSecrets((s) => ({ ...s, [f.name]: 'replace' }))}
                  >
                    {SIEM_FORM_SECRET_REPLACE}
                  </Button>
                  {mode === 'kept' && (
                    <Button
                      type="button"
                      variant="glass"
                      size="sm"
                      onClick={() => setSecrets((s) => ({ ...s, [f.name]: 'removed' }))}
                    >
                      {SIEM_FORM_SECRET_REMOVE}
                    </Button>
                  )}
                </div>
                {help}
              </div>
            );
          }
          if (f.type === 'enum') {
            return (
              <div key={f.name}>
                {label}
                <select
                  id={id}
                  value={values[f.name] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                  className={`${inputCls} bg-transparent`}
                >
                  <option value="">(not set)</option>
                  {(f.enum ?? []).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                {help}
              </div>
            );
          }
          if (isPemField(f)) {
            return (
              <div key={f.name} className="md:col-span-2">
                {label}
                <textarea
                  id={id}
                  rows={4}
                  spellCheck={false}
                  autoComplete="off"
                  value={values[f.name] ?? ''}
                  placeholder="-----BEGIN ...----- or kms://..."
                  onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                  className={`${inputCls} font-mono`}
                />
                {removeSaved}
                {help}
              </div>
            );
          }
          return (
            <div key={f.name}>
              {label}
              <input
                id={id}
                type={f.type === 'secret_ref' ? 'password' : f.type === 'url' ? 'url' : 'text'}
                autoComplete="off"
                value={values[f.name] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                className={`${inputCls} font-mono`}
              />
              {removeSaved}
              {help}
            </div>
          );
        })}
      </div>

      {error && (
        <div className="text-[11px] text-sev-high" role="alert">{error}</div>
      )}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={saving || (!editing && !descriptor)} data-testid="siem-form-save">
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
          {editing ? SIEM_FORM_SAVE_EDIT : SIEM_FORM_SAVE_ADD}
        </Button>
        <Button type="button" variant="glass" size="sm" onClick={onCancel}>{SIEM_FORM_CANCEL}</Button>
      </div>
    </form>
  );
}
